/**
 * CrossValidationPanel — Compara Proyecto ↔ Comparables ↔ Benchmark externo (INEGI/DENUE/SNIIV).
 *
 * Consumes: GET /api/market/cross-validation/{project_id}?scenario_id={id}
 * Cada fila muestra 3 fuentes lado a lado + divergencia % con severidad + botón para aplicar sugerencia.
 */
import { useMarketCrossValidation } from '../lib/queries';
import { Loader2, ShieldCheck, AlertTriangle, Info, ArrowRightLeft, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

function fmtVal(v, unit) {
  if (v == null) return '—';
  if (unit === '$') return '$' + new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 }).format(v);
  if (unit === '%') return `${Number(v).toFixed(1)}%`;
  return new Intl.NumberFormat('es-MX', { maximumFractionDigits: 1 }).format(v) + (unit ? ` ${unit}` : '');
}

function DivergenceBadge({ pct, severity, testId }) {
  if (pct == null) {
    return <span data-testid={testId} className="text-[10px]" style={{ color: 'var(--text-muted)' }}>—</span>;
  }
  const COLOR = {
    critical: { color: 'var(--danger)', bg: 'var(--danger-soft)' },
    warning: { color: 'var(--warning-color)', bg: 'var(--warning-soft)' },
    ok: { color: 'var(--success)', bg: 'var(--success-soft)' },
    'n/a': { color: 'var(--text-muted)', bg: 'var(--bg-hover)' },
  }[severity] || { color: 'var(--text-muted)', bg: 'var(--bg-hover)' };

  const Icon = Math.abs(pct) < 0.1 ? Minus : (pct > 0 ? TrendingUp : TrendingDown);

  return (
    <Badge data-testid={testId} className="text-[10px] tabular-nums gap-1" style={{ background: COLOR.bg, color: COLOR.color, border: 'none' }}>
      <Icon className="w-2.5 h-2.5" />
      {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
    </Badge>
  );
}

function ConfidenceHeader({ score, label, critical, warning, comparablesCount, hasExternal, externalSource }) {
  const color = score >= 80 ? 'var(--success)' : score >= 55 ? 'var(--warning-color)' : 'var(--danger)';
  const circumference = 2 * Math.PI * 32;
  const offset = circumference - (score / 100) * circumference;

  return (
    <Card className="rounded-xl border-border/50 shadow-sm" data-testid="cross-validation-header">
      <CardContent className="p-5">
        <div className="flex items-center gap-5">
          <div className="relative shrink-0" style={{ width: 80, height: 80 }}>
            <svg width="80" height="80" viewBox="0 0 80 80">
              <circle cx="40" cy="40" r="32" fill="none" stroke="var(--bg-hover)" strokeWidth="6" />
              <circle
                cx="40" cy="40" r="32"
                fill="none" stroke={color} strokeWidth="6" strokeLinecap="round"
                strokeDasharray={circumference} strokeDashoffset={offset}
                transform="rotate(-90 40 40)"
                style={{ transition: 'stroke-dashoffset 0.8s ease' }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xl font-display font-bold tabular-nums" data-testid="cross-validation-score" style={{ color }}>{score}</span>
              <span className="text-[9px]" style={{ color: 'var(--text-muted)' }}>/100</span>
            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-4 h-4" style={{ color }} />
              <span className="text-sm font-display font-semibold" style={{ color: 'var(--text-primary)' }}>{label}</span>
            </div>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Comparación cruzada entre tus supuestos, comparables capturados y benchmarks externos.
            </p>
            <div className="flex items-center gap-3 mt-2 text-[10px]">
              {critical > 0 && (
                <span className="flex items-center gap-1" style={{ color: 'var(--danger)' }}>
                  <AlertTriangle className="w-3 h-3" /> {critical} crítico{critical !== 1 ? 's' : ''}
                </span>
              )}
              {warning > 0 && (
                <span className="flex items-center gap-1" style={{ color: 'var(--warning-color)' }}>
                  <AlertTriangle className="w-3 h-3" /> {warning} advertencia{warning !== 1 ? 's' : ''}
                </span>
              )}
              <span style={{ color: 'var(--text-muted)' }}>· {comparablesCount} comparables</span>
              {hasExternal && (
                <span style={{ color: 'var(--text-muted)' }}>· benchmark: <strong>{externalSource || 'referencia'}</strong></span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ValidationRow({ row, onApplySuggestion, canApply }) {
  const isMissing = row.project == null && row.comparables == null && row.benchmark == null;
  if (isMissing) return null;

  return (
    <tr data-testid={`crossval-row-${row.key}`} className="border-b" style={{ borderColor: 'var(--border-subtle)' }}>
      {/* Label */}
      <td className="py-3 pr-3">
        <div className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{row.label}</div>
        <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{row.key}</div>
      </td>

      {/* Project value */}
      <td className="py-3 px-2 text-right">
        <div className="text-xs font-semibold tabular-nums" data-testid={`crossval-${row.key}-project`} style={{ color: row.project != null ? 'var(--text-primary)' : 'var(--text-muted)' }}>
          {fmtVal(row.project, row.unit)}
        </div>
      </td>

      {/* Comparables value */}
      <td className="py-3 px-2 text-right">
        <div className="text-xs font-semibold tabular-nums" data-testid={`crossval-${row.key}-comparables`} style={{ color: row.comparables != null ? 'var(--market-primary)' : 'var(--text-muted)' }}>
          {fmtVal(row.comparables, row.unit)}
        </div>
      </td>

      {/* Divergence proj vs comp */}
      <td className="py-3 px-2 text-center">
        <DivergenceBadge
          pct={row.diff_project_vs_comparables_pct}
          severity={row.severity_vs_comparables}
          testId={`crossval-${row.key}-diff-vs-comp`}
        />
      </td>

      {/* Benchmark value */}
      <td className="py-3 px-2 text-right">
        <div className="text-xs font-semibold tabular-nums" data-testid={`crossval-${row.key}-benchmark`} style={{ color: row.benchmark != null ? 'var(--info)' : 'var(--text-muted)' }}>
          {fmtVal(row.benchmark, row.unit)}
        </div>
      </td>

      {/* Divergence comp vs bench */}
      <td className="py-3 px-2 text-center">
        <DivergenceBadge
          pct={row.diff_comparables_vs_benchmark_pct}
          severity={row.severity_vs_benchmark}
          testId={`crossval-${row.key}-diff-vs-bench`}
        />
      </td>

      {/* Action */}
      <td className="py-3 pl-2 text-right">
        {row.suggested_assumption_key && row.comparables != null && canApply && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-[10px] gap-1"
            data-testid={`crossval-apply-${row.key}`}
            onClick={() => onApplySuggestion(row.suggested_assumption_key, row.comparables, row.label)}
          >
            <ArrowRightLeft className="w-3 h-3" />
            Aplicar
          </Button>
        )}
      </td>
    </tr>
  );
}

export function CrossValidationPanel({ projectId, scenarioId, onApplyToScenario }) {
  const { data, isLoading, isError, error } = useMarketCrossValidation(projectId, scenarioId);

  if (!scenarioId) {
    return (
      <Card className="rounded-xl border-border/50" data-testid="crossval-no-scenario">
        <CardContent className="py-8 text-center">
          <Info className="w-6 h-6 mx-auto mb-2" style={{ color: 'var(--info)', opacity: 0.6 }} />
          <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Selecciona un escenario</p>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>La validación cruzada compara los supuestos del escenario contra el mercado.</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="crossval-loading">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--market-primary)' }} />
      </div>
    );
  }

  if (isError) {
    return (
      <Card className="rounded-xl border-border/50" data-testid="crossval-error">
        <CardContent className="py-8 text-center">
          <AlertTriangle className="w-6 h-6 mx-auto mb-2" style={{ color: 'var(--warning-color)' }} />
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Error al cargar la validación cruzada</p>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{error?.message || 'Intenta de nuevo'}</p>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="cross-validation-panel">
      <ConfidenceHeader
        score={data.confidence_score}
        label={data.confidence_label}
        critical={data.critical_count}
        warning={data.warning_count}
        comparablesCount={data.comparables_count}
        hasExternal={data.has_external}
        externalSource={data.external_source}
      />

      <Card className="rounded-xl border-border/50 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-subtle)' }}>
                <th className="text-left py-3 pl-5 pr-3 text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                  Métrica
                </th>
                <th className="text-right py-3 px-2 text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                  Tu proyecto
                </th>
                <th className="text-right py-3 px-2 text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--market-primary)' }}>
                  Comparables
                </th>
                <th className="text-center py-3 px-2 text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                  Δ
                </th>
                <th className="text-right py-3 px-2 text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--info)' }}>
                  Benchmark
                </th>
                <th className="text-center py-3 px-2 text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                  Δ
                </th>
                <th className="text-right py-3 pl-2 pr-5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                  Acción
                </th>
              </tr>
            </thead>
            <tbody className="px-5">
              {(data.rows || []).map(row => (
                <ValidationRow
                  key={row.key}
                  row={row}
                  onApplySuggestion={onApplyToScenario}
                  canApply={!!onApplyToScenario}
                />
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="text-[10px] text-center" style={{ color: 'var(--text-muted)' }}>
        <span className="font-medium">Lectura:</span>{' '}
        <span style={{ color: 'var(--success)' }}>Verde &lt; 10%</span> ·{' '}
        <span style={{ color: 'var(--warning-color)' }}>Ámbar 10-25%</span> ·{' '}
        <span style={{ color: 'var(--danger)' }}>Rojo ≥ 25%</span>
      </div>
    </div>
  );
}

export default CrossValidationPanel;
